/**
 * Client state + API for "request access to a linked doc".
 *
 * The server owns the source of truth (table `doc_access_requests`). We
 * keep a local Set<docId> of the current user's pending requests so views
 * can render the stub in a "request pending" state without hitting the
 * network on every composite pass.
 *
 * Population:
 *   - boot: `hydrate()` calls GET /api/docs/access-requests/mine.
 *   - after POST /api/docs/:id/request-access succeeds: add to set.
 *   - on VAULT_INDEX_UPDATED: drop entries for any docId the user now has
 *     (the owner granted — request was consumed).
 */

import { emit, Events } from "../core/events.js";
import { t } from "../../shared/i18n/index.js";

const pending = new Set();
// The wrapped key the vault held for each requested map when the request was
// made (null: none). A grant always brings a new one; until the vault's key
// differs, the request is still pending. (A map the account was removed
// from keeps its old key in the vault until recovered: any vault save used
// to clear its request.)
const keyAtRequest = new Map();
let hydrated = false;

/** Hydrate the pending set from the server. Safe to call multiple times. */
export async function hydrate() {
  try {
    const res = await fetch("/api/docs/access-requests/mine", {
      credentials: "include",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    if (!res.ok) {
      console.warn("[AccessRequests] hydrate failed:", res.status);
      return;
    }
    const body = await res.json();
    pending.clear();
    for (const r of body.requests || []) {
      if (r.doc_id) pending.add(r.doc_id);
    }
    for (const docId of [...keyAtRequest.keys()]) if (!pending.has(docId)) keyAtRequest.delete(docId);
    hydrated = true;
    emit(Events.ACCESS_REQUESTS_UPDATED, { pending: new Set(pending) });
  } catch (err) {
    console.warn("[AccessRequests] hydrate error:", err);
  }
}

/**
 * Submit a request for access to a doc.
 *
 * No title is sent (S-M1, review 2026-09-24): the title the requester sees
 * on the stub would reach the server in plaintext. The owner's client shows
 * the title from its own vault. A second argument (older callers passed the
 * title) is ignored.
 * @param {string} docId
 * @param {string|null} [currentWrappedDEK] the key the vault holds for it now, if any
 * @returns {Promise<{ok: boolean, status: number, error?: string}>}
 */
export async function requestAccess(docId, currentWrappedDEK = null) {
  if (!docId) return { ok: false, status: 400, error: "no docId" };
  try {
    const res = await fetch(`/api/docs/${encodeURIComponent(docId)}/request-access`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: "{}",
    });
    if (res.status === 201) {
      pending.add(docId);
      keyAtRequest.set(docId, currentWrappedDEK || null);
      emit(Events.ACCESS_REQUESTS_UPDATED, { pending: new Set(pending) });
      return { ok: true, status: 201 };
    }
    // The server never answers 429 here: repeated requests refresh the
    // existing one (201), and the per-user cap answers 400.
    const body = await res.json().catch(() => ({}));
    return { ok: false, status: res.status, error: body.error || `HTTP ${res.status}` };
  } catch (err) {
    console.warn("[AccessRequests] requestAccess error:", err);
    return { ok: false, status: 0, error: err?.message || t("share.accessRequest.networkError") };
  }
}

/** True if we've already requested access to this doc (locally). */
export function isPending(docId) {
  return pending.has(docId);
}

/** Snapshot the current pending set (Set<string>). */
export function getPendingSet() {
  return new Set(pending);
}

/**
 * Requests loaded from the server (hydrate) for maps the vault already holds
 * a key for: that key is the one before any grant.
 * @param {Array<{docId: string, wrappedDEK?: string}>} docs - vault entries
 */
export function noteVaultKeys(docs) {
  for (const doc of docs || []) {
    if (doc?.docId && pending.has(doc.docId) && !keyAtRequest.has(doc.docId)) {
      keyAtRequest.set(doc.docId, doc.wrappedDEK || null);
    }
  }
}

/**
 * Requests granted since they were made: the vault now holds a different key
 * for the map than when it was requested (the server consumed the request
 * on grant). Called on each vault update.
 * @param {Array<{docId: string, wrappedDEK?: string}>} docs - vault entries
 */
export function resolveGranted(docs) {
  for (const doc of docs || []) {
    if (!doc?.docId || !pending.has(doc.docId) || !doc.wrappedDEK) continue;
    if (doc.wrappedDEK !== (keyAtRequest.get(doc.docId) ?? null)) markResolved(doc.docId);
  }
}

/**
 * Drop an id from the pending set — called when the vault shows the user
 * now has access (server auto-consumed the request on grant).
 */
export function markResolved(docId) {
  keyAtRequest.delete(docId);
  if (pending.delete(docId)) {
    emit(Events.ACCESS_REQUESTS_UPDATED, { pending: new Set(pending) });
  }
}

/** Clear all — e.g., on logout. */
export function reset() {
  pending.clear();
  keyAtRequest.clear();
  hydrated = false;
}

export function isHydrated() {
  return hydrated;
}
